/**
 * Vivi Store: recorrido continuo entre colores en el carrusel de la galeria de producto.
 *
 * La galeria ya llega filtrada por color desde Liquid (ver
 * snippets/product-media-gallery-content.liquid): solo se muestran las fotos cuyo texto
 * alternativo termina en "#<color>", mas las fotos comunes (sin "#").
 *
 * Este modulo cierra el circulo. Cuando el cliente esta en la ultima foto del color y avanza
 * una mas, selecciona el siguiente color en el selector de variantes; el tema vuelve a pedir la
 * seccion y reemplaza <media-gallery> con las fotos de ese color. Al reves: desde la primera
 * foto, retroceder pasa al color anterior y la galeria se abre en su ultima foto.
 * El orden es el de la opcion "Color" del producto y el recorrido es circular.
 *
 * Solo aplica al carrusel. En escritorio la galeria puede estar en cuadricula, y ahi el tema
 * esconde <slideshow-component> por CSS: sin carrusel visible no hay salto y la cuadricula
 * queda intacta.
 *
 * Se engancha desde fuera, envolviendo metodos de <slideshow-component>, para no modificar los
 * assets del tema (mismo enfoque que assets/variant-availability.js).
 */

/** Nombre de la opcion de producto que agrupa las fotos, en minusculas. */
const COLOR_OPTION_NAME = 'color';

/** Pixeles que hay que arrastrar, estando ya en el borde, para que el gesto salte de color. */
const SWIPE_THRESHOLD = 60;

/** Desplazamiento vertical a partir del cual el gesto se considera scroll de pagina. */
const VERTICAL_TOLERANCE = 12;

/** Margen de tolerancia (px) para dar el scroller por tocando fondo. */
const EDGE_TOLERANCE = 2;

/** Ventana en la que se vigila la llegada de la galeria nueva tras cambiar de color. */
const WATCH_WINDOW_MS = 4000;

/** Ventana en la que se ignoran nuevos saltos mientras el tema re-renderiza la seccion. */
const SWITCH_GUARD_MS = 1500;

/** Cuadros durante los que se insiste en dejar la galeria nueva en su ultima foto. */
const REWIND_FRAMES = 20;

/** Red de seguridad (ms) por si la pestana no esta pintando y requestAnimationFrame no corre. */
const REWIND_FALLBACK_MS = 150;

/** Fecha limite hasta la que hay un cambio de color en vuelo. @type {number} */
let switchingUntil = 0;

const now = () => Date.now();

/**
 * @param {unknown} value
 * @returns {string}
 */
const normalize = (value) => String(value ?? '').trim().toLowerCase();

/**
 * @param {Element} element
 * @returns {boolean}
 */
const isVisible = (element) => element.getClientRects().length > 0;

/**
 * El carrusel de una galeria de producto, o null si el elemento no lo es.
 * @param {Element | null} element
 * @returns {any}
 */
function gallerySlideshow(element) {
  const slideshow = element?.closest('slideshow-component');
  if (!slideshow || !slideshow.closest('media-gallery')) return null;
  // Las tarjetas de producto tienen su propio carrusel y su propio selector: no son asunto nuestro.
  if (slideshow.closest('product-card')) return null;
  return slideshow;
}

/**
 * Las fotos de esta galeria estan etiquetadas por color ("... #verde")? Si ninguna lo esta, el
 * producto no usa el agrupado y el carrusel se deja tal cual.
 * @param {Element} slideshow
 * @returns {boolean}
 */
const isColorGrouped = (slideshow) => Boolean(slideshow.querySelector('img[alt*="#"]'));

/**
 * Los controles de la opcion "Color" del selector de variantes que manda sobre esta galeria,
 * en el orden en que el producto define los valores.
 * @param {Element} slideshow
 * @returns {Element[]}
 */
function colorControls(slideshow) {
  const scope = slideshow.closest('.shopify-section, dialog') ?? document;
  const picker =
    scope.querySelector('variant-picker[ref="mainVariantPicker"]') ?? scope.querySelector('variant-picker');
  if (!picker) return [];

  const controls = Array.from(
    picker.querySelectorAll('input[type="radio"][data-option-name], select option[data-option-name]')
  );

  return controls.filter(
    (control) => normalize(/** @type {HTMLElement} */ (control).dataset.optionName) === COLOR_OPTION_NAME
  );
}

/**
 * @param {Element} control
 * @returns {boolean}
 */
const isChosen = (control) =>
  control instanceof HTMLOptionElement ? control.selected : /** @type {HTMLInputElement} */ (control).checked;

/**
 * Selecciona un valor igual que lo haria el cliente, para que el tema re-renderice la seccion.
 * @param {Element} control
 * @returns {boolean} Si se pudo aplicar.
 */
function choose(control) {
  if (control instanceof HTMLOptionElement) {
    const select = control.closest('select');
    if (!select) return false;

    select.value = control.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  /** @type {HTMLInputElement} */ (control).click();
  return true;
}

/**
 * Pasa al color contiguo (circular) del producto al que pertenece este carrusel.
 * @param {Element} slideshow
 * @param {number} direction - 1 hacia adelante, -1 hacia atras.
 * @returns {boolean} Si se disparo el cambio de color.
 */
function stepColor(slideshow, direction) {
  const controls = colorControls(slideshow);
  if (controls.length < 2) return false;

  const currentIndex = controls.findIndex(isChosen);
  if (currentIndex === -1) return false;

  const target = controls[(currentIndex + direction + controls.length) % controls.length];
  if (!target || target === controls[currentIndex]) return false;

  switchingUntil = now() + SWITCH_GUARD_MS;
  // Al entrar a un color viniendo hacia atras, su galeria debe abrirse en la ultima foto.
  watchForNewGallery(direction < 0);

  if (choose(target)) return true;

  switchingUntil = 0;
  stopWatching();
  return false;
}

/**
 * Intenta saltar de color. Quien llama ya comprobo que el carrusel esta en el borde.
 * @param {Element} slideshow
 * @param {number} direction
 * @returns {boolean} Si el carrusel no debe hacer su movimiento normal.
 */
function takeOver(slideshow, direction) {
  if (!gallerySlideshow(slideshow)) return false;
  // Un cambio ya esta en vuelo: traga la accion en vez de dar la vuelta dentro del mismo color.
  if (now() < switchingUntil) return true;
  if (!isColorGrouped(slideshow)) return false;

  return stepColor(slideshow, direction);
}

/**
 * @param {any} slideshow
 * @param {number} direction
 * @returns {boolean}
 */
function atEdge(slideshow, direction) {
  if (!isVisible(slideshow)) return false;

  const slides = slideshow.slides;
  if (!slides?.length) return false;

  return direction > 0 ? slideshow.current >= slides.length - 1 : slideshow.current <= 0;
}

/**
 * @param {Element} scroller
 * @returns {{ atStart: boolean, atEnd: boolean }}
 */
function scrollEdges(scroller) {
  const max = scroller.scrollWidth - scroller.clientWidth;

  return {
    atStart: scroller.scrollLeft <= EDGE_TOLERANCE,
    atEnd: scroller.scrollLeft >= max - EDGE_TOLERANCE,
  };
}

/* -------------------------------------------------------------------------- */
/* Gestos: deslizar con el dedo o arrastrar con el raton                       */
/* -------------------------------------------------------------------------- */

/**
 * @typedef {object} Gesture
 * @property {any} slideshow
 * @property {Element} scroller
 * @property {number} startX
 * @property {number} startY
 * @property {boolean} fromStart - El carrusel ya estaba en la primera foto al empezar.
 * @property {boolean} fromEnd - El carrusel ya estaba en la ultima foto al empezar.
 * @property {boolean} done
 */

/** @type {Gesture | null} */
let gesture = null;

const endGesture = () => {
  gesture = null;
};

/**
 * @param {Event} event
 * @param {number} x
 * @param {number} y
 */
function startGesture(event, x, y) {
  endGesture();

  const target = event.target;
  if (!(target instanceof Element)) return;

  const slideshow = gallerySlideshow(target);
  if (!slideshow || !isVisible(slideshow)) return;

  const scroller = slideshow.querySelector('slideshow-slides');
  if (!scroller) return;

  // El calculo de bordes asume lectura de izquierda a derecha.
  if (getComputedStyle(scroller).direction === 'rtl') return;

  const edges = scrollEdges(scroller);

  gesture = {
    slideshow,
    scroller,
    startX: x,
    startY: y,
    fromStart: edges.atStart && atEdge(slideshow, -1),
    fromEnd: edges.atEnd && atEdge(slideshow, 1),
    done: false,
  };
}

/**
 * @param {number} x
 * @param {number} y
 */
function moveGesture(x, y) {
  if (!gesture || gesture.done) return;

  const dx = gesture.startX - x;
  const dy = Math.abs(gesture.startY - y);

  // Scroll vertical de la pagina: este gesto no es del carrusel.
  if (dy > Math.abs(dx) && dy > VERTICAL_TOLERANCE) return endGesture();
  if (Math.abs(dx) < SWIPE_THRESHOLD) return;

  const direction = dx > 0 ? 1 : -1;

  // Solo cuenta si el carrusel ya estaba topado en esa direccion antes de empezar el gesto:
  // asi un deslizamiento largo que simplemente llega a la ultima foto no salta de color.
  const startedAtEdge = direction > 0 ? gesture.fromEnd : gesture.fromStart;
  if (!startedAtEdge) return;

  const edges = scrollEdges(gesture.scroller);
  if (direction > 0 ? !edges.atEnd : !edges.atStart) return;

  gesture.done = true;
  takeOver(gesture.slideshow, direction);
  endGesture();
}

document.addEventListener(
  'touchstart',
  (event) => {
    if (event.touches.length !== 1) return endGesture();
    const touch = event.touches[0];
    if (touch) startGesture(event, touch.clientX, touch.clientY);
  },
  { capture: true, passive: true }
);

document.addEventListener(
  'touchmove',
  (event) => {
    if (event.touches.length !== 1) return;
    const touch = event.touches[0];
    if (touch) moveGesture(touch.clientX, touch.clientY);
  },
  { capture: true, passive: true }
);

document.addEventListener('touchend', endGesture, { capture: true, passive: true });
document.addEventListener('touchcancel', endGesture, { capture: true, passive: true });

// El arrastre con raton lo mueve el propio tema con scrollLeft, asi que basta con seguir el puntero.
document.addEventListener(
  'pointerdown',
  (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    startGesture(event, event.clientX, event.clientY);
  },
  true
);

document.addEventListener(
  'pointermove',
  (event) => {
    if (event.pointerType !== 'mouse') return;
    moveGesture(event.clientX, event.clientY);
  },
  true
);

document.addEventListener('pointerup', endGesture, true);
document.addEventListener('pointercancel', endGesture, true);

/* -------------------------------------------------------------------------- */
/* La galeria nueva: el tema reemplaza <media-gallery> entero                  */
/* -------------------------------------------------------------------------- */

/**
 * Deja la galeria recien montada en su ultima foto. Las diapositivas del producto no llevan
 * atributo slide-id, asi que `initial-slide` no sirve de nada: hay que llamar a select() una vez
 * que el componente termino de armarse, e insistir unos cuadros porque el propio montaje del
 * carrusel coloca la primera foto y puede llegar despues.
 * @param {any} slideshow
 */
function rewindToLastSlide(slideshow) {
  let attempts = 0;

  const apply = () => {
    attempts++;
    // El cliente ya esta arrastrando: no le quites el carrusel de las manos.
    if (!slideshow.isConnected || slideshow.hasAttribute('dragging')) return false;

    const last = (slideshow.slides?.length ?? 0) - 1;
    if (last > 0 && slideshow.current !== last) slideshow.select(last, undefined, { animate: false });

    return true;
  };

  /** @param {number} frame */
  const onFrame = (frame) => {
    if (!apply() || frame >= REWIND_FRAMES) return;
    requestAnimationFrame(() => onFrame(frame + 1));
  };

  // Dos cuadros de espera para que el montaje del carrusel ocurra antes.
  requestAnimationFrame(() => requestAnimationFrame(() => onFrame(0)));

  setTimeout(() => {
    if (attempts === 0) apply();
  }, REWIND_FALLBACK_MS);
}

/** @type {MutationObserver | null} */
let galleryObserver = null;

/** @type {number} */
let watchTimer = 0;

function stopWatching() {
  galleryObserver?.disconnect();
  galleryObserver = null;
  clearTimeout(watchTimer);
  watchTimer = 0;
}

/**
 * Espera a que el tema inserte la galeria del color nuevo.
 *
 * Tiene que ser un MutationObserver: los callbacks de ciclo de vida de un elemento
 * personalizado (connectedCallback y compania) se leen del prototipo cuando se define el
 * elemento, asi que envolverlos despues no tendria ningun efecto.
 *
 * @param {boolean} rewind - Si la galeria nueva debe abrirse en su ultima foto.
 */
function watchForNewGallery(rewind) {
  stopWatching();

  galleryObserver = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (!(node instanceof Element)) continue;

        const gallery = node.matches('media-gallery') ? node : node.querySelector('media-gallery');
        if (!gallery) continue;

        // Ya hay galeria nueva: el cambio de color termino.
        switchingUntil = 0;

        if (!rewind) return stopWatching();

        const slideshow = gallery.querySelector('slideshow-component');
        if (slideshow) rewindToLastSlide(slideshow);

        // Seguimos vigilando: al cambiar de color el tema puede re-renderizar una segunda vez
        // (por ejemplo si variant-availability.js corrige la talla).
        return;
      }
    }
  });

  galleryObserver.observe(document.documentElement, { childList: true, subtree: true });
  watchTimer = setTimeout(stopWatching, WATCH_WINDOW_MS);
}

/* -------------------------------------------------------------------------- */
/* Flechas y controles: envolvemos next()/previous() del componente            */
/* -------------------------------------------------------------------------- */

customElements.whenDefined('slideshow-component').then(() => {
  const prototype = customElements.get('slideshow-component')?.prototype;

  if (!prototype || typeof prototype.next !== 'function' || typeof prototype.previous !== 'function') {
    console.warn('[vivi] slideshow-component no expone next/previous; el recorrido por colores queda desactivado');
    return;
  }

  const { next, previous } = prototype;

  prototype.next = function (event, options) {
    if (atEdge(this, 1) && takeOver(this, 1)) {
      event?.preventDefault();
      return;
    }

    return next.call(this, event, options);
  };

  prototype.previous = function (event, options) {
    if (atEdge(this, -1) && takeOver(this, -1)) {
      event?.preventDefault();
      return;
    }

    return previous.call(this, event, options);
  };
});
